import React, { useState } from 'react';
import { analyzeWebsite } from '../utils/openaiHelpers';
import { Spinner } from './ui/Spinner';
interface IntakeFormProps {
  onSubmit: (data: any) => void;
}
export const IntakeForm: React.FC<IntakeFormProps> = ({
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    website: '',
    challenge: '',
    target: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [websiteAnalysis, setWebsiteAnalysis] = useState<any>(null);
  const [analyzeStatus, setAnalyzeStatus] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleAnalyzeWebsite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.website) {
      setError('Please enter a website URL');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setAnalyzeStatus('Fetching website content...');
      // Add http:// prefix if not present
      let url = formData.website;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      setAnalyzeStatus('Analyzing website content...');
      const websiteData = await analyzeWebsite(url);
      setWebsiteAnalysis(websiteData);
    } catch (err) {
      setError(`Failed to analyze website: ${err.message || 'Please check the URL or try again later.'}`);
      console.error(err);
    } finally {
      setLoading(false);
      setAnalyzeStatus('');
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!websiteAnalysis) {
      setError('Please analyze the website first');
      return;
    }
    onSubmit({
      ...formData,
      companyProfile: websiteAnalysis
    });
  };
  return <div data-id="element-76">
      <h2 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-77">
        Business Information
      </h2>
      <p className="text-gray-600 mb-8" data-id="element-78">
        Please provide your details and your business website to begin the audit
        process. We'll analyze your website to customize the assessment for your
        specific business.
      </p>
      <form onSubmit={handleSubmit} data-id="element-79">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-80">
          <div data-id="element-81">
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-82">
              Full Name*
            </label>
            <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-83" />
          </div>
          <div data-id="element-84">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-85">
              Email Address*
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-86" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-87">
          <div data-id="element-88">
            <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-89">
              Mobile Number
            </label>
            <input type="tel" id="mobile" name="mobile" value={formData.mobile} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-90" />
          </div>
          <div data-id="element-91">
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-92">
              Business Website*
            </label>
            <div className="flex" data-id="element-93">
              <input type="text" id="website" name="website" value={formData.website} onChange={handleChange} placeholder="www.example.com" required className="w-full px-4 py-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500" data-id="element-94" />
              <button type="button" onClick={handleAnalyzeWebsite} disabled={loading} className="bg-blue-600 text-white px-4 py-2 rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-95">
                {loading ? <Spinner size="sm" data-id="element-96" /> : 'Analyze'}
              </button>
            </div>
            {analyzeStatus && <p className="mt-2 text-sm text-blue-600" data-id="element-97">{analyzeStatus}</p>}
          </div>
        </div>
        <div className="mb-8" data-id="element-98">
          <label htmlFor="challenge" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-99">
            Biggest Business Challenge*
          </label>
          <textarea id="challenge" name="challenge" value={formData.challenge} onChange={handleChange} required rows={3} placeholder="What's your biggest business challenge right now?" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-100"></textarea>
        </div>
        <div className="mb-8" data-id="element-101">
          <label htmlFor="target" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-102">
            Target Result to Achieve*
          </label>
          <textarea id="target" name="target" value={formData.target} onChange={handleChange} required rows={3} placeholder="What specific result or target are you looking to achieve?" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-103"></textarea>
        </div>
        {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6" data-id="element-104">
            <p className="text-red-700" data-id="element-105">{error}</p>
          </div>}
        {websiteAnalysis && <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6" data-id="element-106">
            <h3 className="font-medium text-green-800 mb-2" data-id="element-107">
              Website Analysis Complete
            </h3>
            <div className="text-sm text-green-700 grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-108">
              <p data-id="element-109">
                <strong data-id="element-110">Industry:</strong> {websiteAnalysis.industry}
              </p>
              <p data-id="element-111">
                <strong data-id="element-112">Business Size:</strong> {websiteAnalysis.businessSize}
              </p>
              <p data-id="element-113">
                <strong data-id="element-114">Customer Type:</strong> {websiteAnalysis.customerType}
              </p>
              <p data-id="element-115">
                <strong data-id="element-116">Positioning:</strong> {websiteAnalysis.positioning}
              </p>
              {websiteAnalysis.productsServices && <p data-id="element-117">
                  <strong data-id="element-118">Products/Services:</strong>{' '}
                  {websiteAnalysis.productsServices}
                </p>}
              {websiteAnalysis.targetAudience && <p data-id="element-119">
                  <strong data-id="element-120">Target Audience:</strong>{' '}
                  {websiteAnalysis.targetAudience}
                </p>}
              {websiteAnalysis.summary && <p className="col-span-2 mt-2" data-id="element-121">
                  <strong data-id="element-122">Summary:</strong> {websiteAnalysis.summary}
                </p>}
            </div>
          </div>}
        <div className="flex justify-end" data-id="element-123">
          <button type="submit" disabled={loading || !websiteAnalysis} className={`px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${loading || !websiteAnalysis ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-124">
            Continue to Self-Assessment
          </button>
        </div>
      </form>
    </div>;
};